/**
 * How far a brand new practice has got along its way in, for the handful of
 * things that are owed to one and to nobody else — the first of them being the
 * note on the dashboard that this app is at its best on a wide screen.
 *
 * ## Two stages, because the way in has two parts
 *
 * Signing up is not arriving. A new account goes from the consent screen to the
 * code, and from there to the free trial — which `OfferTrialFirst` holds the
 * dashboard behind until it is settled one way or the other. So "has signed up"
 * is true for the whole of that journey, and anything shown on the strength of
 * it alone would be shown in the middle of it.
 *
 * Hence `markSignedUp` at the account's creation, and `markWayInFinished` at
 * the end of the trial or the paid checkout — the last screen before the
 * sidebar appears for the first time. Only the second one arms anything, and
 * only for a visit the first one marked: an established practice buying another
 * term reaches the same success screen, and is not new.
 *
 * ## Why `sessionStorage`
 *
 * A sign-up is one journey in one tab, and the fact belongs to that journey and
 * dies with it. It survives what has to be survived: a reload, and the
 * top-level redirect out to Google and back, which is the one leg of the route
 * where the running app is thrown away. It does not survive the tab being
 * closed, which is exactly right — the next visit is a sign-in.
 */

/** Single key, holding which stage the visit has reached. */
const ARRIVAL_KEY = "naurist:signup-arrival";

/** Signed up; still somewhere between the consent screen and the dashboard. */
const SIGNED_UP = "signed-up";
/** Signed up and through the trial or the checkout: the dashboard is next. */
const ARRIVED = "arrived";

type Stage = typeof SIGNED_UP | typeof ARRIVED | null;

/**
 * Mirror of the stored value, so the answer is stable during a render rather
 * than a storage read per component asking. Written by the marks below too, so
 * the screens that run after them in the same page load see the change without
 * going back to storage.
 */
let stage: Stage | undefined;

// Storage can throw outright — Safari private mode, or a browser with site data
// blocked. All that costs is a new account's welcome, which is not worth
// failing a sign-up over, so every one of these swallows it.
const read = (): Stage => {
  if (stage === undefined) {
    try {
      const stored = window.sessionStorage.getItem(ARRIVAL_KEY);
      stage = stored === SIGNED_UP || stored === ARRIVED ? stored : null;
    } catch {
      stage = null;
    }
  }
  return stage;
};

const write = (next: Stage): void => {
  stage = next;
  try {
    if (next === null) window.sessionStorage.removeItem(ARRIVAL_KEY);
    else window.sessionStorage.setItem(ARRIVAL_KEY, next);
  } catch {
    /* not fatal — see above */
  }
};

/**
 * Records that this visit is a sign-up.
 *
 * Called as the account is created and before anything that might not come
 * back — the Google route leaves the app outright from there.
 */
export const markSignedUp = (): void => write(SIGNED_UP);

/**
 * Records that the way in is finished: the trial has started, or the plan has
 * been paid for.
 *
 * A no-op for anyone who did not sign up on this visit, which is what keeps an
 * established practice buying another term from being welcomed as a new one.
 * Also a no-op once it has already been recorded, so a screen that says it on
 * every render costs nothing.
 */
export const markWayInFinished = (): void => {
  if (read() === SIGNED_UP) write(ARRIVED);
};

/**
 * Whether a practice that signed up on this visit has just finished its way in
 * and is reaching the app for the first time.
 *
 * False for the whole of the journey before that, and false for every ordinary
 * sign-in.
 */
export const welcomePending = (): boolean => read() === ARRIVED;

/**
 * Spends it.
 *
 * For whoever has now shown the new practice what it was being kept for, so the
 * rest of the visit — and a reload in the middle of it — is an ordinary one.
 */
export const clearWelcome = (): void => write(null);

/**
 * Whether this visit signed up and has not finished its way in yet: somewhere
 * between the consent screen and the dashboard, which `OfferTrialFirst` holds
 * it short of until the trial is settled.
 *
 * The complement of `welcomePending` rather than its opposite — that one asks
 * whether the way in has just *ended*, this one whether it is still running —
 * and both are false for an ordinary sign-in, which has no way in at all.
 *
 * What it is for: the furniture a screen draws only for somebody who is
 * already inside the app. A new practice buying a term lands on the ordinary
 * checkout, inside the shell, having never seen the dashboard — see
 * `BillingCheckoutPage`, which is the one screen of the way in that is also a
 * screen of the app.
 */
export const wayInPending = (): boolean => read() === SIGNED_UP;
